import React, { useEffect, useState } from 'react';
import {
  AlertTriangle,
  Bug,
  CheckCircle2,
  RotateCcw,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { AnalyticsPeriod, ResolvedPeriod } from '../PeriodPicker';
import { QualityApi, QualityKpisResponse } from './qualityApi';
import { KpiHintPopover, KpiHintPopoverProps } from './KpiHintPopover';

interface QualityKpiCardsProps {
  period: AnalyticsPeriod | null;
  resolvedPeriod: ResolvedPeriod | null;
}

/**
 * Rich, click-to-open hints for each KPI card. Each one has:
 *  - one-sentence "what" explanation,
 *  - "How it's calculated" bullets,
 *  - "Setup checklist" — numbered steps that match the badges in
 *    `QualitySettingsPanel`. The popover's CTA deep-links to the right step.
 */
const HINTS: Record<'newBugs' | 'ratio' | 'pass' | 'reopen', Omit<KpiHintPopoverProps, 'id'>> = {
  newBugs: {
    label: 'New bugs',
    what: 'How many bugs your team created in the selected Period. A smaller number is better. Bigger teams make more bugs, so also check Bug-to-Story ratio.',
    formula: [
      <>
        We count every issue whose <strong>type</strong> is in your bug list and was{' '}
        <strong>created</strong> in the Period.
      </>,
      <>Status does not matter — open and closed bugs are both counted.</>,
    ],
    steps: [
      {
        n: 1,
        text: (
          <>
            Choose which Jira <strong>issue types</strong> count as a bug (Bug, Defect, Incident…).
          </>
        ),
        anchorId: 'quality-step-1',
      },
      {
        n: 5,
        text: (
          <>
            Set the <strong>New bugs (max)</strong> target so the card colour matches your goal.
          </>
        ),
        anchorId: 'quality-step-5',
      },
    ],
  },
  ratio: {
    label: 'Bug-to-Story ratio',
    what: 'How many new bugs appear for each new story in the same Period. Watch the trend over time — it tells more than one number.',
    formula: [
      <>
        <strong>new bugs ÷ new stories</strong>, both created in the Period.
      </>,
      <>Only Story and User Story types count as a story.</>,
      <>
        Shows <em>n/a</em> when no stories were created in the Period.
      </>,
    ],
    steps: [
      {
        n: 1,
        text: (
          <>
            Check the <strong>bug-like issue types</strong> — they decide what counts as a bug.
          </>
        ),
        anchorId: 'quality-step-1',
      },
      {
        n: 5,
        text: (
          <>
            Set the <strong>Bug-to-Story (max)</strong> target.
          </>
        ),
        anchorId: 'quality-step-5',
      },
    ],
  },
  pass: {
    label: 'First-time pass',
    what: 'How often a story passes QA on the first try, with no real failure. A high number means clean work is leaving development.',
    formula: [
      <>
        <strong>Bottom number:</strong> stories from the Period that ever entered one of your{' '}
        <strong>QA statuses</strong>.
      </>,
      <>
        <strong>Top number:</strong> stories that <em>never</em> made a real visit to a{' '}
        <strong>Failure status</strong>. A visit only counts if the issue stayed there longer than
        the minimum time you set — this filters out mis-clicks.
      </>,
      <>Needs Jira status history. Run Full Update after you choose the statuses.</>,
    ],
    steps: [
      {
        n: 2,
        text: (
          <>
            Pick the <strong>QA statuses</strong> (e.g. To Verify, In Testing, Verified). A story
            that enters any of them counts as “reached QA”.
          </>
        ),
        anchorId: 'quality-step-2',
      },
      {
        n: 3,
        text: (
          <>
            Pick the <strong>Failure statuses</strong> (e.g. Verification Failed) and the{' '}
            <strong>minimum minutes</strong> (default 15). Shorter visits are ignored as mis-clicks.
          </>
        ),
        anchorId: 'quality-step-3',
      },
      {
        n: 5,
        text: (
          <>
            Set the <strong>First-time pass (min)</strong> target — usually 80%.
          </>
        ),
        anchorId: 'quality-step-5',
      },
    ],
  },
  reopen: {
    label: 'Reopen rate',
    what: 'How often closed work comes back to the team. A high number can mean weak testing, unclear acceptance criteria, or fragile fixes.',
    formula: [
      <>
        <strong>Bottom number:</strong> issues that reached Done, Verified, Closed, or Resolved
        during the Period.
      </>,
      <>
        <strong>Top number:</strong> issues that moved back from one of those statuses to a{' '}
        <strong>Dev status</strong>.
      </>,
    ],
    steps: [
      {
        n: 4,
        text: (
          <>
            Pick the <strong>Dev statuses</strong>. Going back to any of them counts as a reopen.
          </>
        ),
        anchorId: 'quality-step-4',
      },
      {
        n: 5,
        text: (
          <>
            Set the <strong>Reopen rate (max)</strong> target — usually 5%.
          </>
        ),
        anchorId: 'quality-step-5',
      },
    ],
  },
};

type Tone = 'orange' | 'blue' | 'green' | 'purple';

type TargetStatus = 'ok' | 'at-risk' | 'breached' | null;

export const QualityKpiCards: React.FC<QualityKpiCardsProps> = ({ period, resolvedPeriod }) => {
  const [data, setData] = useState<QualityKpisResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await QualityApi.getKpis({ period, resolvedPeriod }, { comparePrev: true });
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load KPIs');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, resolvedPeriod?.startDate, resolvedPeriod?.endDate]);

  const targets = data?.targets;
  const previous = data?.previous ?? null;
  const reasons = data?.reasons ?? null;

  return (
    <div className="quality-kpi-cards">
      <KpiCard
        id="new-bugs"
        icon={<Bug className="w-6 h-6" />}
        tone="orange"
        label="New bugs"
        hint={HINTS.newBugs}
        value={loading ? '…' : error ? '—' : (data?.newBugs ?? 0)}
        numeric={loading || error ? null : (data?.newBugs ?? 0)}
        prevNumeric={previous?.newBugs ?? null}
        target={targets ? targets.newBugsMax : null}
        kind="max"
        formatValue={(n) => String(n)}
      />
      <KpiCard
        id="bug-to-story"
        icon={<AlertTriangle className="w-6 h-6" />}
        tone="purple"
        label="Bug-to-Story ratio"
        hint={HINTS.ratio}
        value={
          loading
            ? '…'
            : error
              ? '—'
              : data?.bugToStoryRatio != null
                ? data.bugToStoryRatio.toFixed(2)
                : 'n/a'
        }
        numeric={data?.bugToStoryRatio ?? null}
        prevNumeric={previous?.bugToStoryRatio ?? null}
        target={targets ? targets.bugStoryRatioMax : null}
        kind="max"
        formatValue={(n) => n.toFixed(2)}
        reasonText={
          data?.bugToStoryRatio == null
            ? reasons?.bugToStory === 'no-stories'
              ? 'n/a — no stories (only non-Story issues) were created in this Period.'
              : reasons?.bugToStory === 'no-period-issues'
                ? 'n/a — no issues at all were created in this Period. Widen the Period.'
                : null
            : null
        }
      />
      <KpiCard
        id="first-time-pass"
        icon={<CheckCircle2 className="w-6 h-6" />}
        tone="green"
        label="First-time pass"
        hint={HINTS.pass}
        value={
          loading
            ? '…'
            : error
              ? '—'
              : data?.firstTimePassPct != null
                ? `${data.firstTimePassPct}%`
                : 'n/a'
        }
        numeric={data?.firstTimePassPct ?? null}
        prevNumeric={previous?.firstTimePassPct ?? null}
        target={targets ? targets.firstTimePassMin : null}
        kind="min"
        suffix="%"
        formatValue={(n) => `${n}%`}
        reasonText={
          data?.firstTimePassPct == null && reasons?.firstTimePass === 'no-qa-stories'
            ? 'n/a — no stories reached QA in this Period. Run Full Update after configuring Quality → QA statuses.'
            : null
        }
      />
      <KpiCard
        id="reopen-rate"
        icon={<RotateCcw className="w-6 h-6" />}
        tone="blue"
        label="Reopen rate"
        hint={HINTS.reopen}
        value={
          loading
            ? '…'
            : error
              ? '—'
              : data?.reopenRatePct != null
                ? `${data.reopenRatePct}%`
                : 'n/a'
        }
        numeric={data?.reopenRatePct ?? null}
        prevNumeric={previous?.reopenRatePct ?? null}
        target={targets ? targets.reopenRateMax : null}
        kind="max"
        suffix="%"
        formatValue={(n) => `${n}%`}
        reasonText={
          data?.reopenRatePct == null && reasons?.reopenRate === 'no-closed-issues'
            ? 'n/a — no issues reached Done/Verified in this Period, so reopens have nothing to compare against.'
            : null
        }
      />
    </div>
  );
};

interface KpiCardProps {
  /** Stable id (used for popover aria + per-card state). */
  id: string;
  icon: React.ReactNode;
  tone: Tone;
  label: string;
  /** Rich popover content explaining the metric and how to set it up. */
  hint: Omit<KpiHintPopoverProps, 'id'>;
  value: string | number;
  numeric: number | null;
  prevNumeric: number | null;
  target: number | null;
  /** `max` — higher is worse (new bugs, reopen rate). `min` — higher is better (FTP). */
  kind: 'min' | 'max';
  suffix?: string;
  formatValue: (n: number) => string;
  /** Shown below the value when the KPI is `n/a` to explain the cause. */
  reasonText?: string | null;
}

function computeTargetStatus(
  value: number | null,
  target: number | null,
  kind: 'min' | 'max'
): TargetStatus {
  if (value == null || target == null) return null;
  if (kind === 'max') {
    if (value <= target * 0.7) return 'ok';
    if (value <= target) return 'at-risk';
    return 'breached';
  }
  // min: higher is better.
  if (value >= target) return 'ok';
  if (value >= target * 0.9) return 'at-risk';
  return 'breached';
}

const KpiCard: React.FC<KpiCardProps> = ({
  id,
  icon,
  tone,
  label,
  hint,
  value,
  numeric,
  prevNumeric,
  target,
  kind,
  formatValue,
  reasonText,
}) => {
  const status = computeTargetStatus(numeric, target, kind);
  const delta =
    typeof numeric === 'number' && typeof prevNumeric === 'number'
      ? Number((numeric - prevNumeric).toFixed(2))
      : null;
  return (
    <div className={`stat-card-item quality-kpi quality-kpi--${status ?? 'none'}`}>
      <div className={`stat-icon stat-icon-${tone}`}>{icon}</div>
      <div className="stat-details">
        <span className="stat-label">
          {label}
          <KpiHintPopover id={id} {...hint} />
        </span>
        <span className="stat-value">{value}</span>
        {reasonText && (
          <p
            className="quality-kpi__reason"
            style={{
              margin: '4px 0 0',
              fontSize: 11,
              color: 'var(--color-text-secondary, #888)',
              lineHeight: 1.3,
            }}
          >
            {reasonText}
          </p>
        )}
        <div className="quality-kpi__meta">
          {target != null && (
            <span className={`quality-kpi__target quality-kpi__target--${status ?? 'none'}`}>
              {kind === 'max' ? '≤ ' : '≥ '}
              {formatValue(target)}
            </span>
          )}
          {delta != null && (
            <span
              className={`quality-kpi__delta quality-kpi__delta--${deltaTone(delta, kind)}`}
              title="Δ vs previous period"
            >
              {delta > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
              {delta > 0 ? '+' : ''}
              {formatValue(Math.abs(delta))}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

function deltaTone(delta: number, kind: 'min' | 'max'): 'good' | 'bad' | 'flat' {
  if (delta === 0) return 'flat';
  if (kind === 'max') return delta < 0 ? 'good' : 'bad';
  return delta > 0 ? 'good' : 'bad';
}
